import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiError, fmtInput, toNum } from "../lib/api";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { statusMeta, CLIENT_STATUSES } from "../lib/status";
import { categorizeFee, BUCKETS, OCCUPANCY_OPTIONS, FEE_CATEGORIES } from "../lib/concessions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Loader2, Check, Upload, UserCheck, FileText, Send, Mail, Eye, Trash2, Sparkles, Search, Phone, MessageSquare, UserPlus, ChevronDown, ChevronUp, ArrowRightLeft, Zap, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]";

function Fld({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-[0.15em] uppercase text-white/50">{label}</Label>
      {children}
    </div>
  );
}

/* Circular headshot avatar — fetches the protected image, falls back to an initial */
function PersonAvatar({ headshotPath, name, email, size = "h-12 w-12", testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj = null;
    if (headshotPath) {
      api.get(`/files/${headshotPath}`, { responseType: "blob" })
        .then((res) => { obj = URL.createObjectURL(res.data); setSrc(obj); })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [headshotPath]);
  const initial = (name || email || "?").trim()[0]?.toUpperCase() || "?";
  return (
    <div className={`${size} rounded-full overflow-hidden bg-[#ff3b30]/15 text-[#ff3b30] flex items-center justify-center font-display font-bold shrink-0 border border-white/10`}>
      {src ? <img src={src} alt={name || email} className="h-full w-full object-cover" data-testid={testid} /> : initial}
    </div>
  );
}


function ContactRow({ phone, email, testidPrefix }) {
  if (!phone && !email) return null;
  const base = "flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-semibold transition-colors";
  const ghost = "bg-white/[0.06] text-white border border-white/15 hover:bg-white/[0.12]";
  return (
    <div className="flex gap-2 mt-3" data-testid={`${testidPrefix}-row`}>
      {phone && <a href={`tel:${phone}`} data-testid={`${testidPrefix}-call`} className={`${base} bg-[#ff3b30] text-white hover:bg-[#ff5147]`}><Phone className="h-3.5 w-3.5" /> Call</a>}
      {phone && <a href={`sms:${phone}`} data-testid={`${testidPrefix}-text`} className={`${base} ${ghost}`}><MessageSquare className="h-3.5 w-3.5" /> Text</a>}
      {email && <a href={`mailto:${email}`} data-testid={`${testidPrefix}-email`} className={`${base} ${ghost}`}><Mail className="h-3.5 w-3.5" /> Email</a>}
    </div>
  );
}

/* ---------------- Invite agent ---------------- */
function InviteAgent() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!email) return toast.error("Enter an email");
    setLoading(true);
    try {
      await api.post("/admin/invite", { email, name });
      toast.success(`Invite sent to ${email}`);
      setOpen(false);
      setEmail("");
      setName("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-invite-button" className="h-10 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-lg shrink-0">
          <Send className="h-4 w-4 mr-2" /> Invite agent
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#141414] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Invite an agent</DialogTitle>
          <DialogDescription className="text-white/50">
            They will get an email inviting them to create an account in 30 seconds and start issuing pre-approvals
            for their clients.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Fld label="Agent name (optional)">
            <Input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" placeholder="Jane Realtor" />
          </Fld>
          <Fld label="Agent email">
            <Input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" placeholder="agent@brokerage.com" />
          </Fld>
        </div>
        <DialogFooter className="mt-4">
          <Button onClick={send} disabled={loading} data-testid="send-invite-button" className="h-10 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-lg">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Send invite</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Pending requests (invited, not yet joined) ---------------- */
function RequestsTab({ invites, reload }) {
  const [busy, setBusy] = useState("");

  if (invites === null)
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[#ff3b30]" />
      </div>
    );

  const resend = async (email) => {
    setBusy(`r-${email}`);
    try {
      await api.post("/admin/invites/resend", { email });
      toast.success(`Invite re-sent to ${email}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resend the invite");
    } finally {
      setBusy("");
    }
  };

  const remove = async (email) => {
    setBusy(`d-${email}`);
    try {
      await api.delete("/admin/invites", { params: { email } });
      toast.success(`Removed request for ${email}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete the request");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="requests-tab">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-2xl tracking-tight">Pending requests</h2>
          <p className="text-sm text-white/50">Agents you've invited who haven't joined yet — resend or remove.</p>
        </div>
        <InviteAgent />
      </div>

      {invites.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-10 text-center" data-testid="requests-empty">
          <p className="text-white/45 text-sm">No pending invites. When you invite an agent who hasn't signed up yet, they'll appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invites.map((inv) => (
            <div
              key={inv.email}
              className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-xl shadow-black/30 rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`request-row-${inv.email}`}
            >
              <div className="min-w-0">
                <p className="font-display text-lg tracking-tight truncate">{inv.name || inv.email}</p>
                <p className="text-sm text-white/50 truncate">{inv.email}</p>
                {inv.invited_at && (
                  <p className="text-xs text-white/35 mt-1">Invited {(inv.invited_at || "").slice(0, 10)} · awaiting sign-up</p>
                )}
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  onClick={() => resend(inv.email)}
                  disabled={busy === `r-${inv.email}`}
                  data-testid={`request-resend-${inv.email}`}
                  className="h-10 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-lg text-xs"
                >
                  {busy === `r-${inv.email}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Resend</>}
                </Button>
                <Button
                  onClick={() => remove(inv.email)}
                  disabled={busy === `d-${inv.email}`}
                  variant="outline"
                  data-testid={`request-delete-${inv.email}`}
                  className="h-10 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-xs"
                >
                  {busy === `d-${inv.email}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------------- Agents ---------------- */
function AgentClientsList({ agentId, onOpenClient }) {
  const [clients, setClients] = useState(null);
  useEffect(() => {
    api.get(`/admin/agents/${agentId}/clients`).then((r) => setClients(r.data)).catch(() => setClients([]));
  }, [agentId]);

  if (clients === null)
    return <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#ff3b30]" /></div>;
  if (clients.length === 0)
    return <p className="text-xs text-white/40 py-3">No clients loaded for this agent yet.</p>;

  return (
    <div className="space-y-2 pt-1" data-testid={`agent-clients-${agentId}`}>
      {clients.map((c) => (
        <button
          key={c.id}
          onClick={() => onOpenClient(c.id)}
          data-testid={`agent-client-${c.id}`}
          className="w-full text-left bg-black/30 hover:bg-black/50 border border-white/10 rounded-xl px-4 py-3 transition-colors flex flex-col items-start gap-2"
        >
          <div className="w-full">
            <p className="text-sm font-semibold break-words">{c.borrower_name}</p>
            <p className="text-xs text-white/40 break-words">{c.borrower_email}</p>
          </div>
          <span className={`text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border ${statusMeta(c.status).cls}`}>
            {statusMeta(c.status).label}
          </span>
        </button>
      ))}
    </div>
  );
}

function AgentCard({ a, approve, toggle, remove, onLoadClient, onActAs, onOpenClient }) {
  const [expanded, setExpanded] = useState(false);
  const isActive = a.status === "active";
  return (
    <div className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-5" data-testid={`agent-row-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <PersonAvatar headshotPath={a.headshot_path} name={a.name} email={a.email} testid={`agent-avatar-${a.id}`} />
          <div className="min-w-0">
            <p className="font-display text-xl tracking-tight truncate">{a.name}</p>
            <p className="text-sm text-white/45 truncate">{a.email}</p>
            <p className="text-xs text-white/35 mt-1">{a.phone || "No phone"} · {a.client_count} client(s)</p>
          </div>
        </div>
        <span
          className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border shrink-0 ${
            isActive ? "text-emerald-400 border-emerald-400/40" : "text-[#ff3b30] border-[#ff3b30]/40"
          }`}
        >
          {a.status}
        </span>
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
        {a.status === "pending" ? (
          <Button
            onClick={() => approve(a.id)}
            data-testid={`approve-agent-${a.id}`}
            className="h-9 bg-[#ff3b30] hover:bg-[#ff5147] text-white rounded-lg text-sm"
          >
            <UserCheck className="h-4 w-4 mr-1.5" /> Approve
          </Button>
        ) : (
          <span className="text-sm text-white/50">Send pre-approvals</span>
        )}
        <div className="flex items-center gap-3">
          {isActive && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/50">{a.can_send ? "On" : "Off"}</span>
              <Switch
                checked={a.can_send}
                onCheckedChange={(v) => toggle(a.id, v)}
                data-testid={`toggle-agent-${a.id}`}
              />
            </div>
          )}
          <button
            onClick={() => remove(a.id, a.email)}
            data-testid={`remove-agent-${a.id}`}
            title="Remove agent"
            className="text-white/30 hover:text-[#ff3b30] transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isActive && (
        <>
          <Button
            onClick={() => onLoadClient(a.id)}
            data-testid={`load-client-for-${a.id}`}
            className="mt-4 w-full h-12 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl"
          >
            <UserPlus className="h-4 w-4 mr-2" /> Load new client for {a.name.split(" ")[0]}
          </Button>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button
              onClick={() => onActAs(a)}
              variant="outline"
              data-testid={`act-as-${a.id}`}
              className="h-10 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl text-xs"
            >
              <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" /> View as agent
            </Button>
            <Button
              onClick={() => setExpanded((v) => !v)}
              variant="outline"
              data-testid={`view-clients-${a.id}`}
              className="h-10 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl text-xs"
            >
              {expanded ? <ChevronUp className="h-3.5 w-3.5 mr-1.5" /> : <ChevronDown className="h-3.5 w-3.5 mr-1.5" />}
              {expanded ? "Hide clients" : "View clients"}
            </Button>
          </div>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <AgentClientsList agentId={a.id} onOpenClient={onOpenClient} />
            </div>
          )}
        </>
      )}

      <ContactRow phone={a.phone} email={a.email} testidPrefix={`agent-contact-${a.id}`} />
    </div>
  );
}

function AgentsTab({ agents, reload, onLoadClient, onActAs, onOpenClient }) {
  const approve = async (id) => {
    try {
      await api.post(`/admin/agents/${id}/approve`);
      toast.success("Agent approved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const toggle = async (id, val) => {
    try {
      await api.patch(`/admin/agents/${id}/toggle`, { can_send: val });
      toast.success(val ? "Pre-approvals enabled" : "Pre-approvals paused");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email}?\n\nThis permanently deletes their account and their clients so they can re-sign up from scratch.`)) return;
    try {
      await api.delete(`/admin/agents/${id}`);
      toast.success("Agent removed — they can now re-sign up");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const [q, setQ] = useState("");
  if (!agents) return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;

  const term = q.trim().toLowerCase();
  const pending = agents.filter((a) => a.status === "pending");
  const others = agents.filter((a) => a.status !== "pending");
  const filtered = term
    ? others.filter((a) =>
        [a.name, a.email, a.phone].filter(Boolean).some((v) => v.toLowerCase().includes(term))
      )
    : others;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <p className="text-sm text-white/50">Approve agents, control who can send, or invite someone new.</p>
        <InviteAgent />
      </div>

      {pending.length > 0 && (
        <div className="mb-7 rounded-2xl border border-[#ff3b30]/45 bg-[#ff3b30]/10 p-4 sm:p-5" data-testid="pending-approvals">
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff3b30] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#ff3b30]" />
            </span>
            <p className="font-display text-lg tracking-tight">Pending approval ({pending.length})</p>
          </div>
          <p className="text-xs text-white/60 mb-4">These agents signed up and are waiting for your thumbs-up. Approve to let them start sending pre-approvals. 🍭</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pending.map((a) => (
              <AgentCard key={a.id} a={a} approve={approve} toggle={toggle} remove={remove} onLoadClient={onLoadClient} onActAs={onActAs} onOpenClient={onOpenClient} />
            ))}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="agent-search"
            placeholder="Search agents by name, email or phone"
            className={`${inputCls} pl-10`}
          />
        </div>
      )}

      {agents.length === 0 ? (
        <div className="border border-white/10 rounded-2xl py-16 text-center text-white/45" data-testid="no-agents">
          No agents yet — invite one to get started.
        </div>
      ) : others.length === 0 ? (
        <div className="border border-white/10 rounded-2xl py-10 text-center text-white/40" data-testid="no-active-agents">
          No active agents yet — approve a pending request above.
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 rounded-2xl py-12 text-center text-white/45" data-testid="no-agent-results">
          No agents match &ldquo;{q}&rdquo;.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((a) => (
            <AgentCard key={a.id} a={a} approve={approve} toggle={toggle} remove={remove} onLoadClient={onLoadClient} onActAs={onActAs} onOpenClient={onOpenClient} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Clients list ---------------- */
function ClientsTab({ clients, reload }) {
  const [savingId, setSavingId] = useState(null);

  const changeStatus = async (id, status) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/clients/${id}/status`, { status });
      toast.success("Client status updated");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  const [q, setQ] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const deleteClient = async (c) => {
    if (!window.confirm(`Delete ${c.borrower_name}?\n\nThis permanently removes this client and all of their pre-approvals and lightning requests. Use this to clear out a duplicate.`)) return;
    setDeletingId(c.id);
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success(`${c.borrower_name} deleted`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete the client");
    } finally {
      setDeletingId(null);
    }
  };
  const toggleCheckin = async (id, v) => {
    try {
      await api.patch(`/clients/${id}/auto-checkin`, { enabled: v });
      toast.success(v ? "Auto 30-day check-in ON" : "Auto 30-day check-in OFF");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  if (!clients) return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;

  const term = q.trim().toLowerCase();
  const filtered = term
    ? clients.filter((c) =>
        [c.borrower_name, c.borrower_email, c.agent_name].filter(Boolean).some((v) => v.toLowerCase().includes(term))
      )
    : clients;

  return (
    <div>
      {clients.length > 0 && (
        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="client-search"
            placeholder="Search clients by name, email or agent"
            className={`${inputCls} pl-10`}
          />
        </div>
      )}
      {clients.length === 0 ? (
        <p className="text-white/45 py-16 text-center">No clients loaded yet.</p>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 rounded-2xl py-12 text-center text-white/45" data-testid="no-client-results">
          No clients match &ldquo;{q}&rdquo;.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
        <div key={c.id} className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-5" data-testid={`admin-client-${c.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-display text-lg tracking-tight truncate">{c.borrower_name}</p>
              <p className="text-sm text-white/45 truncate">{c.borrower_email}</p>
              <p className="text-xs text-white/35 mt-1">Agent: {c.agent_name}</p>
            </div>
            {savingId === c.id && <Loader2 className="h-4 w-4 animate-spin text-[#ff3b30] shrink-0 mt-1" />}
            <button
              type="button"
              onClick={() => deleteClient(c)}
              disabled={deletingId === c.id}
              data-testid={`client-delete-${c.id}`}
              title="Delete client"
              className="shrink-0 text-white/30 hover:text-[#ff3b30] transition-colors disabled:opacity-40"
            >
              {deletingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-sm">
            <span className="text-white/50">Max</span>
            <span className="font-semibold mpl-tabular">{money(c.max_purchase_price)}</span>
          </div>
          <div className="mt-4 space-y-2">
            <Label className="text-[10px] tracking-[0.18em] uppercase text-white/40 flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 rounded-full ${statusMeta(c.status).dot}`} /> Approval status
            </Label>
            <Select value={c.status || "preapproved"} onValueChange={(v) => changeStatus(c.id, v)}>
              <SelectTrigger className={inputCls} data-testid={`status-select-${c.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
                {CLIENT_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`status-option-${c.id}-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className={`text-xs mt-3 flex items-center gap-1.5 ${c.loan_estimate_path ? "text-emerald-400" : "text-[#ff3b30]"}`}>
            <FileText className="h-3.5 w-3.5" />
            {c.loan_estimate_path ? c.loan_estimate_name || "Loan estimate on file" : "No loan estimate"}
          </p>
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-white/80">Enrolled in 30-day auto-update</p>
              <p className="text-[11px] text-white/40">Nudge the borrower after 30 days</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-white/50">{c.auto_checkin !== false ? "On" : "Off"}</span>
              <Switch
                checked={c.auto_checkin !== false}
                onCheckedChange={(v) => toggleCheckin(c.id, v)}
                data-testid={`checkin-toggle-${c.id}`}
              />
            </div>
          </div>
        </div>
      ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Add client ---------------- */
function AddClientTab({ agents, reload, preselectAgent }) {
  const active = (agents || []).filter((a) => a.status === "active");
  const [form, setForm] = useState({
    agent_id: preselectAgent || "",
    borrower_name: "",
    borrower_email: "",
    borrower_phone: "",
    max_purchase_price: "",
    interest_rate: "",
    apr: "",
    insurance_monthly: "",
    loan_term_years: "30",
    down_payment_pct: "",
    loan_type: "Conventional 30-Yr Fixed",
    status: "preapproved",
    occupancy: "primary",
    closing_costs: [],
    prepaids: "",
    prepaids_items: [],
    seller_concessions: "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setMoney = (k) => (e) => setForm({ ...form, [k]: fmtInput(e.target.value) });
  const MONEY_KEYS = ["max_purchase_price", "insurance_monthly", "prepaids", "seller_concessions"];
  const editList = (listKey, i, key, val) => setForm((prev) => {
    const arr = [...(prev[listKey] || [])];
    arr[i] = { ...arr[i], [key]: key === "amount" ? fmtInput(val) : val };
    return { ...prev, [listKey]: arr };
  });
  const addRow = (listKey) => setForm((prev) => ({ ...prev, [listKey]: [...(prev[listKey] || []), listKey === "closing_costs" ? { label: "", amount: "", category: "third_party" } : { label: "", amount: "" }] }));
  const removeRow = (listKey, i) => setForm((prev) => ({ ...prev, [listKey]: prev[listKey].filter((_, x) => x !== i) }));
  const setCC = (i, key, val) => editList("closing_costs", i, key, val);
  const addCC = () => addRow("closing_costs");
  const removeCC = (i) => removeRow("closing_costs", i);
  const setPP = (i, key, val) => editList("prepaids_items", i, key, val);
  const addPP = () => addRow("prepaids_items");
  const removePP = (i) => removeRow("prepaids_items", i);

  const num = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

  const autofill = async (f) => {
    if (!f) return;
    setFile(f);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/extract-loan-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const x = data.fields || {};
      setForm((prev) => ({
        ...prev,
        borrower_name: x.borrower_name ?? prev.borrower_name,
        borrower_email: x.borrower_email ?? prev.borrower_email,
        borrower_phone: x.borrower_phone ?? prev.borrower_phone,
        max_purchase_price: fmtInput(num(x.max_purchase_price)) || prev.max_purchase_price,
        interest_rate: num(x.interest_rate) || prev.interest_rate,
        apr: num(x.apr) || prev.apr,
        loan_term_years: num(x.loan_term_years) || prev.loan_term_years,
        down_payment_pct: num(x.down_payment_pct) || prev.down_payment_pct,
        insurance_monthly: fmtInput(num(x.insurance_monthly)) || prev.insurance_monthly,
        loan_type: x.loan_type ?? prev.loan_type,
        prepaids: fmtInput(num(x.prepaids)) || prev.prepaids,
        closing_costs: Array.isArray(x.closing_costs) && x.closing_costs.length
          ? x.closing_costs.map((c) => ({ label: String(c.label || ""), amount: fmtInput(String(c.amount ?? "")), category: c.category || categorizeFee(c.label) }))
          : prev.closing_costs,
        prepaids_items: Array.isArray(x.prepaids_items) && x.prepaids_items.length
          ? x.prepaids_items.map((c) => ({ label: String(c.label || ""), amount: fmtInput(String(c.amount ?? "")) }))
          : prev.prepaids_items,
      }));
      toast.success("Fields autofilled from PDF — review, then load the client");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agent_id) return toast.error("Select an agent");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === "closing_costs" || k === "prepaids_items") return;
        if (MONEY_KEYS.includes(k)) { fd.append(k, String(toNum(v))); return; }
        fd.append(k, v);
      });
      const cc = (form.closing_costs || [])
        .filter((c) => (c.label || "").trim() || toNum(c.amount))
        .map((c) => ({ label: c.label || "Fee", amount: toNum(c.amount), category: c.category || categorizeFee(c.label) }));
      fd.append("closing_costs", JSON.stringify(cc));
      const pp = (form.prepaids_items || [])
        .filter((c) => (c.label || "").trim() || toNum(c.amount))
        .map((c) => ({ label: c.label || "Prepaid", amount: toNum(c.amount) }));
      fd.append("prepaids_items", JSON.stringify(pp));
      if (pp.length) fd.set("prepaids", String(pp.reduce((s, c) => s + c.amount, 0)));
      if (file) fd.append("loan_estimate", file);
      await api.post("/admin/clients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Client loaded — agent can now send pre-approvals");
      setForm({ ...form, borrower_name: "", borrower_email: "", max_purchase_price: "", interest_rate: "", down_payment_pct: "", closing_costs: [], prepaids: "", prepaids_items: [], seller_concessions: "" });
      setFile(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 max-w-2xl space-y-5">
      <div className="rounded-xl border border-[#ff3b30]/30 bg-[#ff3b30]/5 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-[#ff3b30]" />
          <p className="font-semibold text-sm">Autofill from a Loan Summary PDF</p>
        </div>
        <p className="text-xs text-white/50 mb-3">
          Upload the lender's loan summary and we'll read the borrower and loan details for you — then just review and load. The PDF is also attached as the loan estimate.
        </p>
        <label className="flex items-center gap-3 bg-black/40 border border-white/15 border-dashed rounded-lg px-4 h-11 cursor-pointer hover:border-[#ff3b30]/50 transition-colors">
          {extracting ? <Loader2 className="h-4 w-4 animate-spin text-[#ff3b30]" /> : <Sparkles className="h-4 w-4 text-[#ff3b30]" />}
          <span className="text-sm text-white/60 truncate">
            {extracting ? "Reading your PDF…" : "Upload loan summary PDF to autofill"}
          </span>
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            disabled={extracting}
            onChange={(e) => autofill(e.target.files?.[0])}
            data-testid="autofill-pdf-input"
          />
        </label>
      </div>

      <Fld label="Assign to agent">
        <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
          <SelectTrigger className={inputCls} data-testid="select-agent">
            <SelectValue placeholder={active.length ? "Choose an agent" : "No active agents yet"} />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
            {active.map((a) => (
              <SelectItem key={a.id} value={a.id} data-testid={`agent-option-${a.id}`}>
                {a.name} — {a.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Fld>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Fld label="Borrower name">
          <Input className={inputCls} value={form.borrower_name} onChange={set("borrower_name")} required data-testid="input-borrower-name" />
        </Fld>
        <Fld label="Borrower email">
          <Input className={inputCls} type="email" value={form.borrower_email} onChange={set("borrower_email")} required data-testid="input-borrower-email" />
        </Fld>
        <Fld label="Borrower phone (for texting)">
          <Input className={inputCls} value={form.borrower_phone} onChange={set("borrower_phone")} data-testid="input-borrower-phone" />
        </Fld>
        <Fld label="Max purchase price ($)">
          <Input className={inputCls} inputMode="numeric" value={form.max_purchase_price} onChange={setMoney("max_purchase_price")} required data-testid="input-max-price" />
        </Fld>
        <Fld label="Interest rate (%)">
          <Input className={inputCls} type="number" step="0.001" value={form.interest_rate} onChange={set("interest_rate")} required data-testid="input-rate" />
        </Fld>
        <Fld label="APR (%)">
          <Input className={inputCls} type="number" step="0.001" value={form.apr} onChange={set("apr")} data-testid="input-apr" />
        </Fld>
        <Fld label="Loan term (years)">
          <Input className={inputCls} type="number" value={form.loan_term_years} onChange={set("loan_term_years")} required data-testid="input-term" />
        </Fld>
        <Fld label="Down payment (%)">
          <Input className={inputCls} type="number" step="0.01" value={form.down_payment_pct} onChange={set("down_payment_pct")} required data-testid="input-down" />
        </Fld>
        <Fld label="Homeowners insurance (monthly $)">
          <Input className={inputCls} inputMode="numeric" value={form.insurance_monthly} onChange={setMoney("insurance_monthly")} data-testid="input-insurance" />
        </Fld>
      </div>

      <div className="border border-white/10 rounded-xl p-4 space-y-3" data-testid="closing-costs-editor">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-white">Closing costs <span className="text-white/40 font-normal">(from the loan summary — editable)</span></p>
            <p className="text-[11px] text-white/40">These itemized fees appear on page 2 of the buyer's loan estimate.</p>
          </div>
          <Button type="button" onClick={addCC} variant="outline" data-testid="add-closing-cost" className="h-8 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-xs">
            + Add fee
          </Button>
        </div>
        {(form.closing_costs || []).length === 0 && (
          <p className="text-xs text-white/35">No fees yet. Upload a loan summary to auto-fill, or add them manually.</p>
        )}
        {(form.closing_costs || []).map((c, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`closing-cost-row-${i}`}>
            <Input className={inputCls + " flex-1 min-w-[9rem]"} placeholder="Fee name (e.g. Title insurance)" value={c.label} onChange={(e) => setCC(i, "label", e.target.value)} data-testid={`cc-label-${i}`} />
            <Input className={inputCls + " w-24"} inputMode="numeric" placeholder="$" value={c.amount} onChange={(e) => setCC(i, "amount", e.target.value)} data-testid={`cc-amount-${i}`} />
            <Select value={c.category || "third_party"} onValueChange={(v) => setCC(i, "category", v)}>
              <SelectTrigger className={inputCls + " w-40"} data-testid={`cc-category-${i}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
                {FEE_CATEGORIES.map((b) => (
                  <SelectItem key={b.key} value={b.key} data-testid={`cc-cat-opt-${i}-${b.key}`}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => removeCC(i)} data-testid={`cc-remove-${i}`} className="text-white/30 hover:text-[#ff3b30] transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="grid grid-cols-1 gap-3 pt-1">
          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-white">Prepaids & prorated items <span className="text-white/40 font-normal">(insurance, taxes, interest)</span></p>
                <p className="text-[11px] text-white/40">These prorated prepaids roll into the buyer's Total Cash to Close.</p>
              </div>
              <Button type="button" onClick={addPP} variant="outline" data-testid="add-prepaid" className="h-8 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg text-xs">
                + Add item
              </Button>
            </div>
            {(form.prepaids_items || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2 mb-2" data-testid={`prepaid-row-${i}`}>
                <Input className={inputCls + " flex-1"} placeholder="e.g. Prepaid interest / Insurance premium / Tax reserves" value={c.label} onChange={(e) => setPP(i, "label", e.target.value)} data-testid={`pp-label-${i}`} />
                <Input className={inputCls + " w-32"} inputMode="numeric" placeholder="$" value={c.amount} onChange={(e) => setPP(i, "amount", e.target.value)} data-testid={`pp-amount-${i}`} />
                <button type="button" onClick={() => removePP(i)} data-testid={`pp-remove-${i}`} className="text-white/30 hover:text-[#ff3b30] transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Fld label="Seller concessions ($)">
            <Input className={inputCls} inputMode="numeric" value={form.seller_concessions} onChange={setMoney("seller_concessions")} data-testid="input-seller-concessions" />
          </Fld>
        </div>
      </div>

      <Fld label="Loan program">
        <Input className={inputCls} value={form.loan_type} onChange={set("loan_type")} data-testid="input-loan-type" />
      </Fld>

      <Fld label="Occupancy (sets max seller concessions)">
        <Select value={form.occupancy} onValueChange={(v) => setForm({ ...form, occupancy: v })}>
          <SelectTrigger className={inputCls} data-testid="input-occupancy">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
            {OCCUPANCY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`occupancy-${o.value}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Fld>

      <Fld label="Approval status">
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger className={inputCls} data-testid="input-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-white/15 text-white">
            {CLIENT_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`add-status-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Fld>

      <Fld label="Loan estimate (PDF)">
        <label className="flex items-center gap-3 bg-black/40 border border-white/15 border-dashed rounded-lg px-4 h-11 cursor-pointer hover:border-white/30 transition-colors">
          <Upload className="h-4 w-4 text-white/50" />
          <span className="text-sm text-white/60 truncate">{file ? file.name : "Upload loan estimate PDF"}</span>
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="input-loan-estimate-file"
          />
        </label>
      </Fld>

      <Button
        type="submit"
        disabled={loading}
        data-testid="create-client-button"
        className="h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl px-8"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load client"}
      </Button>
    </form>
  );
}

/* ---------------- Lender profile ---------------- */
function ImageField({ label, kind, path, busy, onUpload, onDelete, circle }) {
  const inputRef = useRef(null);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let obj = null;
    if (path) {
      api
        .get(`/files/${path}`, { responseType: "blob" })
        .then((res) => {
          obj = URL.createObjectURL(res.data);
          setSrc(obj);
        })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => {
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [path]);

  return (
    <Fld label={label}>
      {path ? (
        <div className="flex items-center gap-4">
          <div
            className={`bg-white border border-white/15 overflow-hidden flex items-center justify-center shrink-0 ${
              circle ? "rounded-full h-16 w-16" : "rounded-lg h-16 w-28 p-1.5"
            }`}
          >
            {src ? (
              <img
                src={src}
                alt={label}
                className={circle ? "h-full w-full object-cover" : "max-h-full max-w-full object-contain"}
                data-testid={`${kind}-thumb`}
              />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-black/40" />
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid={`${kind}-change`}
              className="h-9 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-lg"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onDelete}
              disabled={busy}
              data-testid={`${kind}-delete`}
              className="h-9 border-[#ff3b30]/40 bg-transparent text-[#ff3b30] hover:bg-[#ff3b30]/10 rounded-lg"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid={`upload-${kind}-input`}
          />
        </div>
      ) : (
        <label className="flex items-center gap-3 bg-black/40 border border-white/15 border-dashed rounded-lg px-4 h-11 cursor-pointer hover:border-white/30 transition-colors w-fit pr-6">
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : <Upload className="h-4 w-4 text-white/50" />}
          <span className="text-sm text-white/60">Upload {kind === "logo" ? "logo image" : "headshot photo"}</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid={`upload-${kind}-input`}
          />
        </label>
      )}
    </Fld>
  );
}

function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [logoPath, setLogoPath] = useState(null);
  const [headshotPath, setHeadshotPath] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [headshotBusy, setHeadshotBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/profile").then((r) => {
      setProfile({
        name: r.data.name || "", company_name: r.data.company_name || "", phone: r.data.phone || "",
        email: r.data.email || "", nmls: r.data.nmls || "",
      });
      setLogoPath(r.data.logo_path || null);
      setHeadshotPath(r.data.headshot_path || null);
    });
  }, []);

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/profile", profile);
      toast.success("Lender profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (f) => {
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", f);
      const res = await api.post("/admin/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(res.data.logo_path);
      toast.success("Logo updated — it will appear on generated PDFs");
    } catch (e) {
      toast.error("Logo upload failed");
    } finally {
      setLogoBusy(false);
    }
  };

  const deleteLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/admin/logo");
      setLogoPath(null);
      toast.success("Logo removed");
    } catch (e) {
      toast.error("Could not remove logo");
    } finally {
      setLogoBusy(false);
    }
  };

  const uploadHeadshot = async (f) => {
    if (!f) return;
    setHeadshotBusy(true);
    try {
      const fd = new FormData();
      fd.append("headshot", f);
      const res = await api.post("/admin/headshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeadshotPath(res.data.headshot_path);
      toast.success("Headshot updated — it will appear on generated PDFs");
    } catch (e) {
      toast.error("Headshot upload failed");
    } finally {
      setHeadshotBusy(false);
    }
  };

  const deleteHeadshot = async () => {
    setHeadshotBusy(true);
    try {
      await api.delete("/admin/headshot");
      setHeadshotPath(null);
      toast.success("Headshot removed");
    } catch (e) {
      toast.error("Could not remove headshot");
    } finally {
      setHeadshotBusy(false);
    }
  };

  const previewPdf = async () => {
    setPreviewing(true);
    try {
      await api.put("/admin/profile", profile);
      const res = await api.get("/admin/preview/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const [sending, setSending] = useState(false);
  const sendSamples = async () => {
    setSending(true);
    try {
      await api.put("/admin/profile", profile);
      const { data } = await api.post("/admin/send-samples", {});
      toast.success(`Sample pre-approval + loan quote emailed to ${data.to} 🍭`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send samples");
    } finally {
      setSending(false);
    }
  };

  if (!profile) return <Loader2 className="h-6 w-6 animate-spin text-[#ff3b30] mx-auto my-16" />;

  return (
    <form onSubmit={save} className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 rounded-2xl p-6 max-w-2xl space-y-5">
      <p className="text-sm text-white/50">This information appears on every pre-approval PDF and in borrower emails.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Fld label="Lender name">
          <Input className={inputCls} value={profile.name} onChange={set("name")} data-testid="profile-name" />
        </Fld>
        <Fld label="Company name">
          <Input className={inputCls} value={profile.company_name} onChange={set("company_name")} data-testid="profile-company" />
        </Fld>
        <Fld label="Phone">
          <Input className={inputCls} value={profile.phone} onChange={set("phone")} data-testid="profile-phone" />
        </Fld>
        <Fld label="Email">
          <Input className={inputCls} type="email" value={profile.email} onChange={set("email")} data-testid="profile-email" />
        </Fld>
        <Fld label="NMLS #">
          <Input className={inputCls} value={profile.nmls} onChange={set("nmls")} data-testid="profile-nmls" />
        </Fld>
      </div>

      <ImageField label="Logo (shown on PDFs)" kind="logo" path={logoPath} busy={logoBusy} onUpload={uploadLogo} onDelete={deleteLogo} />
      <ImageField label="Headshot (shown on PDFs)" kind="headshot" path={headshotPath} busy={headshotBusy} onUpload={uploadHeadshot} onDelete={deleteHeadshot} circle />

      <div className="flex flex-col sm:flex-row gap-3">
        <Button type="submit" disabled={saving} data-testid="save-profile-button" className="h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl px-8">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" /> Save profile</>}
        </Button>
        <Button type="button" onClick={previewPdf} disabled={previewing} variant="outline" data-testid="preview-pdf-button" className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl px-8">
          {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="h-4 w-4 mr-2" /> Preview PDF</>}
        </Button>
        <Button type="button" onClick={sendSamples} disabled={sending} variant="outline" data-testid="send-samples-button" className="h-11 border-[#ff3b30]/40 bg-transparent text-[#ff3b30] hover:bg-[#ff3b30]/10 rounded-xl px-8">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" /> Email me samples</>}
        </Button>
      </div>
    </form>
  );
}

/* ---------------- Action center (top of dashboard) ---------------- */
function ActionCenter({ lightning, pendingAgents, accessRequests, onOpenClient, onGoAgents, onGoRequests }) {
  const lreqs = lightning || [];
  const hasAny = lreqs.length > 0 || pendingAgents > 0 || accessRequests > 0;
  if (!hasAny) return null;

  return (
    <div className="mb-8 space-y-4" data-testid="lender-action-center">
      {lreqs.length > 0 && (
        <div className="rounded-2xl border border-[#FFD23F]/50 bg-gradient-to-r from-[#1a1405] via-[#221a02] to-[#1a1405] p-5 shadow-[0_0_28px_rgba(255,210,63,0.28)] mpl-lightning-glow" data-testid="action-lightning">
          <div className="flex items-center gap-3 mb-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#FFD23F] to-[#FF8A00] text-black shadow-[0_0_18px_rgba(255,210,63,0.7)]">
              <Zap className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-xl tracking-tight text-[#FFE480]">
                {lreqs.length} Lightning {lreqs.length === 1 ? "request" : "requests"} waiting
              </p>
              <p className="text-xs text-white/60">Agents need a fast approval — tap Build to jump straight in.</p>
            </div>
          </div>
          <div className="space-y-2">
            {lreqs.slice(0, 6).map((r) => {
              const working = r.status === "working";
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onOpenClient(r.client_id)}
                  data-testid={`action-lightning-${r.id}`}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#FFD23F]/30 bg-black/30 px-4 py-3 text-left hover:border-[#FFD23F]/60 hover:bg-black/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-sm flex items-center gap-1.5 text-white">
                      <Zap className="h-3.5 w-3.5 text-[#FFD23F]" /> {money(r.offer_price)} · {r.borrower_name || "Client"}
                    </p>
                    <p className="text-xs text-white/50 truncate">{r.address} · from {r.requested_by || "agent"}</p>
                  </div>
                  <span className="shrink-0 flex items-center gap-2">
                    {working && (
                      <span className="text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-[#FFD23F]/60 text-[#FFE480]">On it</span>
                    )}
                    <span className="inline-flex items-center h-8 px-3 rounded-lg font-bold text-black text-xs bg-gradient-to-r from-[#FFD23F] to-[#FF8A00]">
                      Build <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(pendingAgents > 0 || accessRequests > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pendingAgents > 0 && (
            <button
              type="button"
              onClick={onGoAgents}
              data-testid="action-pending-agents"
              className="flex items-center justify-between gap-3 rounded-2xl border border-[#ff3b30]/40 bg-[#ff3b30]/10 px-5 py-4 text-left hover:bg-[#ff3b30]/15 transition-colors mpl-pulse-glow"
            >
              <div className="flex items-center gap-3">
                <UserCheck className="h-5 w-5 text-[#ff3b30]" />
                <div>
                  <p className="font-semibold text-sm text-white">{pendingAgents} agent {pendingAgents === 1 ? "sign-up" : "sign-ups"} to approve</p>
                  <p className="text-xs text-white/55">Review and approve so they can start sending.</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-white/40 shrink-0" />
            </button>
          )}
          {accessRequests > 0 && (
            <button
              type="button"
              onClick={onGoRequests}
              data-testid="action-access-requests"
              className="flex items-center justify-between gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-4 text-left hover:bg-white/[0.08] transition-colors"
            >
              <div className="flex items-center gap-3">
                <UserPlus className="h-5 w-5 text-white/70" />
                <div>
                  <p className="font-semibold text-sm text-white">{accessRequests} new access {accessRequests === 1 ? "request" : "requests"}</p>
                  <p className="text-xs text-white/55">Realtors asking to join your workspace.</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-white/40 shrink-0" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function LenderDashboard() {
  const [agents, setAgents] = useState(null);
  const [clients, setClients] = useState(null);
  const [invites, setInvites] = useState(null);
  const [lightning, setLightning] = useState([]);
  const [tab, setTab] = useState("agents");
  const [preAgent, setPreAgent] = useState("");
  const navigate = useNavigate();
  const { startActingAs } = useAuth();

  const reload = useCallback(() => {
    api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => setAgents([]));
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => setInvites([]));
    api.get("/admin/lightning-requests").then((r) => setLightning(r.data || [])).catch(() => setLightning([]));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const pending = (agents || []).filter((a) => a.status === "pending").length;
  const requestCount = (invites || []).length;

  const loadClientFor = (agentId) => {
    setPreAgent(agentId);
    setTab("add");
  };
  const actAsAgent = (agent) => {
    startActingAs(agent);
    navigate("/dashboard");
  };
  const openClient = (clientId) => navigate(`/lender/client/${clientId}`);

  return (
    <Shell title="Lender control" subtitle="Approve agents, load clients & loan estimates, and control who can send.">
      <ActionCenter
        lightning={lightning}
        pendingAgents={pending}
        accessRequests={requestCount}
        onOpenClient={openClient}
        onGoAgents={() => setTab("agents")}
        onGoRequests={() => setTab("requests")}
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#141414]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40 p-1 rounded-xl mb-8 !flex !w-full !flex-nowrap justify-start gap-1 !h-auto overflow-x-auto">
          {[
            ["agents", `Agents${pending ? ` (${pending})` : ""}`],
            ["requests", `Requests${requestCount ? ` (${requestCount})` : ""}`],
            ["clients", "Clients"],
            ["add", "Load client"],
            ["profile", "Profile"],
          ].map(([v, label]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="flex-1 whitespace-nowrap data-[state=active]:bg-[#ff3b30] data-[state=active]:text-white text-white/60 rounded-lg px-2 sm:px-4 text-xs sm:text-sm"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="agents">
          <AgentsTab agents={agents} reload={reload} onLoadClient={loadClientFor} onActAs={actAsAgent} onOpenClient={openClient} />
        </TabsContent>
        <TabsContent value="requests">
          <RequestsTab invites={invites} reload={reload} />
        </TabsContent>
        <TabsContent value="clients">
          <ClientsTab clients={clients} reload={reload} />
        </TabsContent>
        <TabsContent value="add">
          <AddClientTab key={preAgent || "none"} agents={agents} reload={reload} preselectAgent={preAgent} />
        </TabsContent>
        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
